import { createSelector } from '@ngrx/store';
import { WaypointType } from 'src/graphql/generated/graphql.generated';
import { AddressRecord } from 'src/store/generated/address-record.generated';
import { WaypointRecord } from '../generated/waypoint-record.generated';
import { hasLatLng } from '../model-helpers';
import {
  selectAllPositionReports,
  selectAllTripLegs,
  selectAllWaypoints,
} from './entity-store-selectors';
import { selectNonCancelledTripLegsByTripId } from './tripleg-selectors';
import {
  selectVehicleDispositionByCardNumber,
  selectVehicleDispositionByUnitId,
} from './vehicle-disposition-selector';

export function selectWaypointById(waypointId: string) {
  return createSelector(selectAllWaypoints, (waypoints) => {
    return waypoints.find((w) => w.id === waypointId);
  });
}

export function selectFirstWaypointByTripAndType(tripId: string, type: WaypointType) {
  return createSelector(selectAllTripLegs, selectAllWaypoints, (legs, waypoints) => {
    return legs
      .filter((l) => l.tripId === tripId)
      .flatMap((leg) =>
        waypoints
          .filter((w) => w.type === type)
          .filter((w) => leg.startingWaypointId === w.id || leg.endingWaypointId === w.id),
      )
      .first(undefined);
  });
}

export function selectWaypointsTripId(tripId: string) {
  return createSelector(
    selectNonCancelledTripLegsByTripId(tripId),
    selectAllWaypoints,
    (legs, waypoints) => {
      return waypoints.filter((w) =>
        legs.find((l) => l.startingWaypointId === w.id || l.endingWaypointId === w.id),
      );
    },
  );
}
export function selectWaypointsWithLatLngsByTripId(tripId: string) {
  return createSelector(
    selectNonCancelledTripLegsByTripId(tripId),
    selectAllWaypoints,
    (legs, waypoints) => {
      return waypoints
        .filter((w) =>
          legs.find((l) => l.startingWaypointId === w.id || l.endingWaypointId === w.id),
        )
        .filter((w) => hasLatLng(w));
    },
  );
}

export function selectBestKnownLocationForVehicleByUnitId(unitId: string) {
  return createSelector(
    selectCurrentWaypointForVehicleByUnitId(unitId),
    selectLatestPositionReportForVehicleByUnitId(unitId),
    selectStartingWaypointOfCurrentTripLegByUnitId(unitId),
    (currentWaypoint, positionReport, startingWaypoint) => {
      if (currentWaypoint != null) {
        return {
          description: 'Current waypoint',
          waypoint: currentWaypoint,
        };
      }
      if (positionReport != null) {
        return {
          description: 'Last position report',
          waypoint: new WaypointRecord({
            id: 'latest-position-report',
            name: 'Latest position report',
            abbreviation: 'Latest position report',
            type: WaypointType.Other,
            arrivingType: null,
            address: new AddressRecord({
              streetAddress: null,
              city: null,
              country: null,
              county: null,
              postalCode: null,
              region: null,
              gpsId: null,
              coordinates: positionReport.coordinates,
            }),
            timezoneCd: positionReport.timezoneCd,
            companyId: positionReport.companyId,
            dateLastUpdated: positionReport.dateLastUpdated,
            dateCreated: positionReport.dateCreated,
            isRotorWingLz: null,
            arrivalTime: null,
            departureTime: null,
            locationId: null,
            isPatientDropoff: null,
            isPatientPickup: null,
            estimatedDurationId: null,
            currentEtaId: null,
            dateDeleted: null,
            lastUpdatedById: null,
          }),
        };
      }
      if (startingWaypoint != null) {
        return {
          description: 'Last waypoint',
          waypoint: startingWaypoint,
        };
      }
      return undefined;
    },
  );
}

export function selectCurrentTripLegByUnitId(unitId: string) {
  return createSelector(selectVehicleDispositionByUnitId(unitId), selectAllTripLegs, (vd, legs) => {
    return vd != null && vd.currentTripLegId != null
      ? legs.find((l) => l.id === vd.currentTripLegId)
      : undefined;
  });
}

export function selectStartingWaypointOfCurrentTripLegByUnitId(unitId: string) {
  return createSelector(
    selectCurrentTripLegByUnitId(unitId),
    selectAllWaypoints,
    (tripLeg, waypoints) => {
      return tripLeg != null && tripLeg.startingWaypointId != null
        ? waypoints.find((w) => w.id === tripLeg.startingWaypointId)
        : undefined;
    },
  );
}

export function selectCurrentWaypointForVehicleByUnitId(unitId: string) {
  return createSelector(
    selectVehicleDispositionByUnitId(unitId),
    selectAllWaypoints,
    (disposition, waypoints) => {
      // NOTE: In the future we may need to look at the current trip leg
      // in addition to current waypoint (they should be mutally exclusive)
      const result =
        disposition != null && disposition.currentWaypointId != null
          ? waypoints.get(disposition.currentWaypointId)
          : undefined;
      return result;
    },
  );
}

export function selectLatestPositionReportForVehicleByUnitId(unitId: string) {
  return createSelector(
    selectVehicleDispositionByUnitId(unitId),
    selectAllPositionReports,
    (disposition, positionReports) => {
      const result =
        disposition != null && disposition.latestPositionReportId != null
          ? positionReports.get(disposition.latestPositionReportId)
          : undefined;
      return result;
    },
  );
}

export function selectCurrentWaypointForVehicleByCardNumber(cardNumber: string) {
  return createSelector(
    selectVehicleDispositionByCardNumber(cardNumber),
    selectAllWaypoints,
    (disposition, waypoints) => {
      const result =
        disposition != null && disposition.currentWaypointId != null
          ? waypoints.get(disposition.currentWaypointId)
          : undefined;
      return result;
    },
  );
}
